import React, {useMemo} from "react";
import {Box, Typography} from "@mui/material";
import {Link} from "react-router-dom";
import {
    catalogItemGalleryStackMq,
    catalogItemMediaStackMq,
    catalogItemNarrowMq,
    getModuleCompositionDimension,
    getModuleCompositionDisplayName,
    sortSpecificationModuleCompositions,
} from "../../utils/compositionMediaUtils";

const specificationFieldsCompactMq = "@media (max-width: 700px)";
const specificationStackedMq = "@media (max-width: 560px)";

const formatSpecificationDimension = (value) =>
    value == null || value === "" ? "—" : `${value} мм`;

const formatSpecificationDimensionsGroup = (row) =>
    ["width", "height", "depth"]
        .map((key) => formatSpecificationDimension(getModuleCompositionDimension(row, key)))
        .join(" х ");

const specificationDesktopGridSx = {
    display: "grid",
    width: "100%",
    maxWidth: "100%",
    gridTemplateColumns: "minmax(0, 1fr) repeat(3, minmax(36px, max-content))",
    columnGap: 0,
    [specificationStackedMq]: {
        display: "none",
    },
};

const specificationMobileListSx = {
    display: "none",
    flexDirection: "column",
    width: "100%",
    gap: "8px",
    [specificationStackedMq]: {
        display: "flex",
    },
};

const specificationCellBaseSx = {
    display: "flex",
    alignItems: "center",
    minHeight: "40px",
    padding: "8px 12px",
    boxSizing: "border-box",
    borderRight: "1px solid #EAEBEA",
    [catalogItemMediaStackMq]: {
        minHeight: "36px",
        padding: "6px 8px",
    },
    [catalogItemGalleryStackMq]: {
        minHeight: "32px",
        padding: "4px 6px",
    },
    [catalogItemNarrowMq]: {
        padding: "2px 4px",
    },
};

const specificationNameCellSx = {
    ...specificationCellBaseSx,
    minWidth: 0,
    overflow: "hidden",
    paddingRight: "12px",
    [catalogItemGalleryStackMq]: {
        overflow: "visible",
        alignItems: "flex-start",
    },
    [specificationFieldsCompactMq]: {
        overflow: "visible",
        alignItems: "flex-start",
    },
};

const specificationDimensionCellSx = {
    ...specificationCellBaseSx,
    justifyContent: "flex-end",
    padding: "8px 12px",
    whiteSpace: "nowrap",
    [catalogItemMediaStackMq]: {
        padding: "6px 8px",
    },
    [catalogItemGalleryStackMq]: {
        padding: "4px 6px",
        alignItems: "flex-start",
    },
    [catalogItemNarrowMq]: {
        padding: "2px 4px",
    },
    [specificationFieldsCompactMq]: {
        alignItems: "flex-start",
    },
};

const specificationHeaderDimensionCellSx = {
    ...specificationDimensionCellSx,
    alignItems: "center",
    [catalogItemGalleryStackMq]: {
        alignItems: "center",
    },
    [specificationFieldsCompactMq]: {
        alignItems: "center",
    },
};

const specificationHeaderNameCellSx = {
    ...specificationNameCellSx,
    alignItems: "center",
    [catalogItemGalleryStackMq]: {
        alignItems: "center",
    },
    [specificationFieldsCompactMq]: {
        alignItems: "center",
    },
};

const specificationLastDimensionCellSx = {
    ...specificationDimensionCellSx,
    borderRight: "none",
};

const specificationHeaderLastDimensionCellSx = {
    ...specificationHeaderDimensionCellSx,
    borderRight: "none",
};

const specificationHeaderTextSx = {
    fontSize: "16px",
    fontWeight: 600,
    minWidth: 0,
    whiteSpace: "nowrap",
    lineHeight: 1.2,
    [catalogItemMediaStackMq]: {
        fontSize: "14px",
    },
    [catalogItemNarrowMq]: {
        fontSize: "16px",
    },
};

const specificationNameTextSx = {
    ...specificationHeaderTextSx,
    width: "100%",
    [catalogItemGalleryStackMq]: {
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
    },
    [specificationFieldsCompactMq]: {
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
    },
};

const specificationNameLinkSx = {
    color: "#006CCC",
    fontWeight: 400,
    textDecoration: "none",
    "&:hover": {
        textDecoration: "underline",
    },
};

const specificationValueTextSx = {
    fontSize: "16px",
    fontWeight: 400,
    whiteSpace: "nowrap",
    lineHeight: 1.2,
    [catalogItemMediaStackMq]: {
        fontSize: "14px",
    },
    [catalogItemNarrowMq]: {
        fontSize: "16px",
    },
};

const specificationMobileRowSx = {
    width: "100%",
    padding: "8px 12px",
    boxSizing: "border-box",
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    [catalogItemMediaStackMq]: {
        padding: "6px 8px",
    },
    [catalogItemNarrowMq]: {
        padding: "6px 8px",
    },
};

const specificationMobileDimensionsTextSx = {
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: 1.2,
    color: (theme) => theme.palette.text.main,
    whiteSpace: "normal",
    [catalogItemMediaStackMq]: {
        fontSize: "14px",
    },
};

const specificationRowContentsSx = {
    display: "contents",
};

const getRowBackgroundColor = (index) =>
    index < 0 ? "#F5F6FA" : index % 2 === 0 ? "#F5F6FA" : "white";

const getDesktopCellSx = (cellIndex, cellsCount, isHeader) => {
    if (cellIndex === 0) {
        return isHeader ? specificationHeaderNameCellSx : specificationNameCellSx;
    }
    if (cellIndex === cellsCount - 1) {
        return isHeader
            ? specificationHeaderLastDimensionCellSx
            : specificationLastDimensionCellSx;
    }
    return isHeader ? specificationHeaderDimensionCellSx : specificationDimensionCellSx;
};

const getDesktopCellRadiusSx = (cellIndex, cellsCount) => ({
    borderRadius:
        cellIndex === 0
            ? "8px 0 0 8px"
            : cellIndex === cellsCount - 1
                ? "0 8px 8px 0"
                : 0,
    [catalogItemGalleryStackMq]: {
        borderRadius:
            cellIndex === 0
                ? "6px 0 0 6px"
                : cellIndex === cellsCount - 1
                    ? "0 6px 6px 0"
                    : 0,
    },
});

const OrderSpecificationModulesTable = ({moduleCompositions, moduleLinksEnabled = false}) => {
    const rows = useMemo(
        () =>
            (Array.isArray(moduleCompositions) ? moduleCompositions : [])
                .slice()
                .sort(sortSpecificationModuleCompositions),
        [moduleCompositions],
    );

    if (rows.length === 0) {
        return (
            <Typography sx={{fontSize: "16px", color: "text.secondary"}}>
                Модули пока не добавлены.
            </Typography>
        );
    }

    const renderModuleName = (row) => {
        const name = getModuleCompositionDisplayName(row);
        const moduleId = row?.module?.id ?? row?.moduleId;

        if (!moduleLinksEnabled || moduleId == null) {
            return (
                <Typography title={name} sx={specificationNameTextSx}>
                    {name}
                </Typography>
            );
        }

        return (
            <Typography title={name} sx={specificationNameTextSx}>
                <Box
                    component={Link}
                    to={`/organization/module/${moduleId}`}
                    sx={specificationNameLinkSx}
                >
                    {name}
                </Box>
            </Typography>
        );
    };

    const renderDesktopRow = (cells, {rowKey, stripeIndex, isHeader = false}) => {
        const backgroundColor = getRowBackgroundColor(stripeIndex);

        return (
            <Box key={rowKey} sx={specificationRowContentsSx}>
                {cells.map((cell, cellIndex) => (
                    <Box
                        key={cellIndex}
                        sx={{
                            ...getDesktopCellSx(cellIndex, cells.length, isHeader),
                            ...getDesktopCellRadiusSx(cellIndex, cells.length),
                            backgroundColor,
                        }}
                    >
                        {cell}
                    </Box>
                ))}
            </Box>
        );
    };

    return (
        <Box sx={{width: "100%"}}>
            <Box sx={specificationDesktopGridSx}>
                {renderDesktopRow(
                    [
                        <Typography key="name" sx={specificationNameTextSx}>
                            Полное наименование
                        </Typography>,
                        <Typography key="width" sx={specificationHeaderTextSx}>
                            Ширина
                        </Typography>,
                        <Typography key="height" sx={specificationHeaderTextSx}>
                            Высота
                        </Typography>,
                        <Typography key="depth" sx={specificationHeaderTextSx}>
                            Глубина
                        </Typography>,
                    ],
                    {rowKey: "header", stripeIndex: -1, isHeader: true},
                )}
                {rows.map((row, index) =>
                    renderDesktopRow(
                        [
                            <React.Fragment key="name">
                                {renderModuleName(row)}
                            </React.Fragment>,
                            <Typography key="width" sx={specificationValueTextSx}>
                                {formatSpecificationDimension(getModuleCompositionDimension(row, "width"))}
                            </Typography>,
                            <Typography key="height" sx={specificationValueTextSx}>
                                {formatSpecificationDimension(getModuleCompositionDimension(row, "height"))}
                            </Typography>,
                            <Typography key="depth" sx={specificationValueTextSx}>
                                {formatSpecificationDimension(getModuleCompositionDimension(row, "depth"))}
                            </Typography>,
                        ],
                        {rowKey: row.id ?? `row-${index}`, stripeIndex: index},
                    ),
                )}
            </Box>
            <Box sx={specificationMobileListSx}>
                {rows.map((row, index) => (
                    <Box
                        key={row.id ?? `mobile-row-${index}`}
                        sx={{
                            ...specificationMobileRowSx,
                            backgroundColor: getRowBackgroundColor(index),
                        }}
                    >
                        {renderModuleName(row)}
                        <Typography sx={specificationMobileDimensionsTextSx}>
                            {formatSpecificationDimensionsGroup(row)}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default OrderSpecificationModulesTable;
